// Leitura do indicador "a portaria foi avisada?" para pedidos e coletas.
//
// O backend grava o resultado do alerta em `alertaMovimentacao`. Interpretar
// esse campo em um só lugar importa porque o indicador aparece em telas
// diferentes: um que mostra "notificada" onde o e-mail não saiu é pior que
// indicador nenhum — dá à operação uma confiança que ela não tem.

const MOTIVOS = {
    'sem-nota-embarque': 'Pedido foi para rota sem Nota de Embarque, que é o documento que dispara o alerta.',
    'sem-destinatarios': 'Nenhum e-mail de portaria cadastrado no Painel Admin.',
    'sem-nota': 'Nota de Embarque não encontrada no momento do envio.',
    'coleta-nao-encontrada': 'Coleta não encontrada no momento do envio.',
    'erro-envio': 'Falha ao enviar o e-mail.',
};

const hora = (valor) => {
    if (!valor) return '';
    const data = new Date(valor);
    return Number.isNaN(data.getTime())
        ? ''
        : data.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

/**
 * @param {Object} doc  Request ou CollectOrder vinda da API
 * @param {Object} [opcoes]
 * @param {boolean} [opcoes.legadoNotificado]
 *   Tratar como notificado quando não existe `alertaMovimentacao` mas existe
 *   sinal antigo de que houve alerta. Usado nas coletas: as que já estavam em
 *   rota quando o campo passou a existir têm coletor gravado e nenhum resumo,
 *   e marcá-las de vermelho seria alarme falso.
 *
 * @returns {{estado:'notificada'|'falhou'|'sem-registro', rotulo:string, detalhe:string}}
 *
 * `sem-registro` e `falhou` são estados diferentes de propósito. "Falhou" é uma
 * afirmação: sabemos que o alerta não saiu, e por quê. "Sem registro" é a
 * ausência de informação — tipicamente movimentação anterior a este controle.
 * Tratar as duas como alarme pintaria de vermelho todo o acumulado no primeiro
 * dia, e indicador que grita em tudo ensina a operação a não olhar para ele.
 */
export function statusAlertaPortaria(doc, { legadoNotificado = false } = {}) {
    const alerta = doc?.alertaMovimentacao;

    if (!alerta || alerta.ok === undefined || alerta.ok === null) {
        if (legadoNotificado) {
            return {
                estado: 'notificada',
                rotulo: 'Notificada',
                detalhe: 'Alerta enviado antes deste indicador existir — sem registro da data.',
            };
        }
        return {
            estado: 'sem-registro',
            rotulo: 'Sem registro',
            detalhe: 'Movimentação sem registro de alerta — anterior a este controle. A partir de agora toda saída grava o resultado.',
        };
    }

    if (alerta.ok) {
        const quando = hora(alerta.enviadoEm);
        const partes = [
            quando ? `Enviado em ${quando}` : 'Alerta enviado',
            alerta.destinatarios?.length ? `para ${alerta.destinatarios.join(', ')}` : '',
            alerta.segundaVia ? '(2ª via)' : '',
            alerta.semAnexo ? '— SEM o documento anexado' : '',
        ].filter(Boolean);

        return {
            estado: 'notificada',
            rotulo: alerta.semAnexo ? 'Notificada (sem anexo)' : 'Notificada',
            detalhe: partes.join(' '),
        };
    }

    return {
        estado: 'falhou',
        rotulo: 'Não notificada',
        detalhe: MOTIVOS[alerta.motivo] || `Falha no alerta: ${alerta.motivo || 'motivo não registrado'}`,
    };
}

/** A portaria precisa ser avisada (ainda) desta movimentação? */
export const precisaNotificar = (doc, opcoes) =>
    statusAlertaPortaria(doc, opcoes).estado !== 'notificada';
